import React, { useCallback, useEffect, useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useCollectionEmojiIcon } from '@jira/polaris__component-collections/src/useCollectionEmojiIcon.tsx';
import type { NavigationView } from '@jira/polaris__component-navigation-store/src/NavigationView.types.tsx';
import { useNavigationCollectionsActions } from '@jira/polaris__component-navigation-collections-store/src/useNavigationCollectionsActions.tsx';
import {
	getCollectionDetailsPageUrl,
	getCollectionViewPageUrl,
} from '@jira/polaris__component-collections/src/getCollectionsPageUrl.tsx';
import {
	ExpandableMenuItem,
	ExpandableMenuItemContent,
	ExpandableMenuItemTrigger,
	type ExpandableMenuItemTriggerProps,
} from '@atlaskit/side-nav-items/expandable-menu-item';
import { JiraProductDiscoveryCollectionAri } from '@atlassian/ari/jira-product-discovery/collection';
import { isNormalClick } from '@jira/navigation-apps__sidebar-common/src/utils/is-normal-click.tsx';
import { useRouterActions } from '@atlassian/react-resource-router';
import { useGetLinkProps } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/get-link-props/index.tsx';
import { useOpenable } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/use-openable/index.tsx';
import { useNestedItemDragAndDrop } from '@atlassian/navigation-drag-and-drop';
import type { NavigationCollection } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-jpd-nav-views/src/common/types.tsx';
import { WithData } from './common/ui/with-data/index.tsx';
import { useIsCollectionSelected } from './common/utils/router.tsx';
import { useRoadmapReorderingIds } from './common/utils/useRoadmapReorderingIds.tsx';
import { ActionButtonsRoadmap } from './ActionButtonsRoadmap.tsx';
import { RoadmapItemViews } from './common/ui/roadmap-item-views/index.tsx';
import type {
	RoadmapItem_collection$key,
	RoadmapItem_collection$data,
} from './__generated__/RoadmapItem_collection.graphql.ts';
import { useDragAndDropReorderingAnalytics } from './useDragAndDropReorderingAnalytics.tsx';

type RoadmapItemProps = {
	collectionQueryRefFragment?: RoadmapItem_collection$key | null;
	currentItems?: Array<{ id: string; label: string }>;
	currentOrder?: Array<{ menuId: string; visible: boolean }>;
	item?: {
		id: string;
		label: string;
	};
	isDraggable?: boolean;
};
export const RoadmapItem = ({
	collectionQueryRefFragment = null,
	currentItems,
	currentOrder,
	item,
	isDraggable = false,
}: RoadmapItemProps): React.JSX.Element | null => {
	const roadmapItemCollectionData = useFragment<RoadmapItem_collection$key>(
		graphql`
			fragment RoadmapItem_collection on JiraProductDiscoveryCollection {
				id @required(action: THROW)
				title @required(action: THROW)
				collectionId @required(action: THROW)
				emoji
				...ActionButtonsRoadmap_collection
			}
		`,
		collectionQueryRefFragment,
	);
	const { push } = useRouterActions();
	const { getLinkProps } = useGetLinkProps();
	const { updateCollection, expandSectionForSelectedCollection } =
		useNavigationCollectionsActions();

	const EmojiIcon = useCollectionEmojiIcon(roadmapItemCollectionData?.emoji ?? undefined);
	const uuid = roadmapItemCollectionData
		? JiraProductDiscoveryCollectionAri.parse(roadmapItemCollectionData.id).collectionId
		: '';
	const isCollectionSelected =
		useIsCollectionSelected(uuid ? { collectionUUID: uuid } : undefined) || false;

	const {
		isOpen: isCollectionExpanded,
		onToggle: onExpansionToggle,
		setIsOpen,
	} = useOpenable(isCollectionSelected);

	useEffect(() => {
		if (isCollectionSelected) {
			expandSectionForSelectedCollection();
			setIsOpen(true);
		}
	}, [expandSectionForSelectedCollection, isCollectionSelected, setIsOpen]);

	const handleExpansionToggle = useCallback(
		(nextIsExpanded: boolean) => {
			onExpansionToggle();
			updateCollection(uuid, { isExpanded: nextIsExpanded });
		},
		[onExpansionToggle, updateCollection, uuid],
	);

	const onViewCreated = useCallback(
		(view: NavigationView) => {
			if (isCollectionSelected) {
				return;
			}
			push(getCollectionViewPageUrl(uuid, view.id));
		},
		[isCollectionSelected, push, uuid],
	);

	const handleRoadmapClick: NonNullable<ExpandableMenuItemTriggerProps['onClick']> = useCallback(
		(event) => {
			if (isNormalClick(event)) {
				event.preventDefault();
			}
		},
		[],
	);

	const navigationCollection: NavigationCollection = useMemo(() => {
		return {
			collectionUUID: uuid,
		};
	}, [uuid]);

	if (!roadmapItemCollectionData) {
		return null;
	}

	if (isDraggable) {
		return (
			<DraggableRoadmapItemContent
				roadmapItemCollectionData={roadmapItemCollectionData}
				uuid={uuid}
				isCollectionSelected={isCollectionSelected}
				isCollectionExpanded={isCollectionExpanded}
				onExpansionToggle={handleExpansionToggle}
				onViewCreated={onViewCreated}
				onClick={handleRoadmapClick}
				navigationCollection={navigationCollection}
				EmojiIcon={EmojiIcon}
				getLinkProps={getLinkProps}
				currentItems={currentItems}
				currentOrder={currentOrder}
				item={item}
			/>
		);
	}

	return (
		<WithData collectionUUID={uuid} onViewCreated={onViewCreated}>
			<ExpandableMenuItem
				isExpanded={isCollectionExpanded}
				onExpansionToggle={handleExpansionToggle}
			>
				<ExpandableMenuItemTrigger
					{...getLinkProps(getCollectionDetailsPageUrl(uuid))}
					elemBefore={<EmojiIcon label={roadmapItemCollectionData.title} fitToHeight={20} />}
					onClick={handleRoadmapClick}
					actionsOnHover={
						<ActionButtonsRoadmap
							collectionUUID={uuid}
							isCollectionSelected={isCollectionSelected}
							isCollectionExpanded={isCollectionExpanded}
							navigationCollection={navigationCollection}
							onViewCreated={onViewCreated}
							collectionQueryRefFragment={roadmapItemCollectionData}
							currentItems={currentItems}
							currentOrder={currentOrder}
							item={item}
						/>
					}
					interactionName="nav4-sidebar-roadmaps"
				>
					{roadmapItemCollectionData.title}
				</ExpandableMenuItemTrigger>
				<ExpandableMenuItemContent>
					<RoadmapItemViews
						isCollectionSelected={isCollectionSelected}
						navigationCollection={navigationCollection}
					/>
				</ExpandableMenuItemContent>
			</ExpandableMenuItem>
		</WithData>
	);
};

type DraggableRoadmapItemContentProps = {
	roadmapItemCollectionData: RoadmapItem_collection$data;
	uuid: string;
	isCollectionSelected: boolean;
	isCollectionExpanded: boolean;
	onExpansionToggle: (nextIsExpanded: boolean) => void;
	onViewCreated: (view: NavigationView) => void;
	onClick: NonNullable<ExpandableMenuItemTriggerProps['onClick']>;
	navigationCollection: NavigationCollection;
	EmojiIcon: ReturnType<typeof useCollectionEmojiIcon>;
	getLinkProps: ReturnType<typeof useGetLinkProps>['getLinkProps'];
	currentItems?: Array<{ id: string; label: string }>;
	currentOrder?: Array<{ menuId: string; visible: boolean }>;
	item?: { id: string; label: string };
};

function DraggableRoadmapItemContent({
	roadmapItemCollectionData,
	uuid,
	isCollectionSelected,
	isCollectionExpanded,
	onExpansionToggle,
	onViewCreated,
	onClick,
	navigationCollection,
	EmojiIcon,
	getLinkProps,
	currentItems,
	currentOrder,
	item,
}: DraggableRoadmapItemContentProps) {
	const { entityAri, ownerAri, group } = useRoadmapReorderingIds();
	const { fireAnalyticsEvent } = useDragAndDropReorderingAnalytics();
	const dragAndDropState = useNestedItemDragAndDrop({
		entityAri,
		ownerAri,
		data: {
			id: roadmapItemCollectionData.id,
			label: roadmapItemCollectionData.title,
		},
		group,
		fireAnalyticsEvent,
		item: undefined,
		isExpandable: true,
		isExpandOrFlyoutOpen: isCollectionExpanded,
		id: roadmapItemCollectionData.id,
		isDragAndDropEnabled: true,
		onExpandFlyoutToggle: (open: boolean) => {
			if (open !== isCollectionExpanded) {
				onExpansionToggle(open);
			}
		},
	});

	return (
		<WithData collectionUUID={uuid} onViewCreated={onViewCreated}>
			<ExpandableMenuItem
				isExpanded={isCollectionExpanded && !dragAndDropState.isDragging}
				onExpansionToggle={onExpansionToggle}
				ref={dragAndDropState.dropTargetRef}
				dropIndicator={dragAndDropState.dropIndicator}
			>
				<ExpandableMenuItemTrigger
					{...getLinkProps(getCollectionDetailsPageUrl(uuid))}
					elemBefore={<EmojiIcon label={roadmapItemCollectionData.title} fitToHeight={20} />}
					onClick={onClick}
					isDragging={dragAndDropState?.isDragging}
					hasDragIndicator
					ref={dragAndDropState.draggableRef}
					actionsOnHover={
						<ActionButtonsRoadmap
							collectionUUID={uuid}
							isCollectionSelected={isCollectionSelected}
							isCollectionExpanded={isCollectionExpanded}
							navigationCollection={navigationCollection}
							onViewCreated={onViewCreated}
							collectionQueryRefFragment={roadmapItemCollectionData}
							currentItems={currentItems}
							currentOrder={currentOrder}
							item={item}
						/>
					}
					interactionName="nav4-sidebar-roadmaps"
				>
					{roadmapItemCollectionData.title}
				</ExpandableMenuItemTrigger>
				<ExpandableMenuItemContent>
					<RoadmapItemViews
						isCollectionSelected={isCollectionSelected}
						navigationCollection={navigationCollection}
					/>
				</ExpandableMenuItemContent>
			</ExpandableMenuItem>
			{dragAndDropState.maybeDragPreview}
		</WithData>
	);
}
